import { useQueryClient } from '@tanstack/react-query'
import { Loader2, User } from 'lucide-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { FacesCard } from '@/components/faces/FacesCard'
import { AddPhotoButton } from '@/components/identities/AddPhotoButton'
import { GraphEntryCard } from '@/components/identities/GraphEntryCard'
import {
  IdentityDetailLayout,
  type IdentityDetailTab,
  type IdentityFaceSections,
} from '@/components/identities/IdentityDetailLayout'
import { IdentityHeaderCard } from '@/components/identities/IdentityHeaderCard'
import { IdentityProfileFields } from '@/components/identities/IdentityProfileFields'
import { IdentityScoreCard } from '@/components/identities/IdentityScoreCard'
import { LinkedDocumentsList } from '@/components/identities/LinkedDocumentsList'
import { PageShell } from '@/components/layout/PageShell'
import { GeneratePdfMenu } from '@/components/pdf-generation/GeneratePdfMenu'
import { Card, CardContent } from '@/components/ui/card'
import { EntityHistoryOf } from '@/features/entity-history/EntityHistory'
import {
  invalidateFaceMatching,
  useFaceMatchingIdentity,
} from '@/features/face-matching/hooks/useFaceMatching'
import {
  CARD_DETAIL_OPTIONS,
  type UseIdentityFacesResult,
  useIdentityFaces,
} from '@/features/face-matching/hooks/useIdentityFaces'
import { IdentityMatches } from '@/features/face-matching/IdentityMatches'
import { IdentityAudio } from '@/features/identities/audio/IdentityAudio'
import {
  type UseAttachIdentityDocumentsResult,
  useAttachIdentityDocuments,
} from '@/features/identities/hooks/useAttachIdentityDocuments'
import {
  type UseIdentityDetailResult,
  useIdentityDetail,
} from '@/features/identities/hooks/useIdentityDetail'
import {
  survivorNamedBy,
  useIdentityPageFollowsMerge,
} from '@/features/identities/hooks/useMergedInto'
import { unassignedFacesKeys } from '@/features/identities/hooks/useUnassignedFaces'
import { IdentityPhotos } from '@/features/identities/IdentityPhotos'
import { calculateIdentityScore, getScoreLabel } from '@/features/identities/identity-score'
import type { IdentityDetail } from '@/features/identities/types'
import { WatchToggle } from '@/features/monitor/WatchToggle'
import { OsintEntityLaunchButton } from '@/features/osint/OsintEntityLaunchButton'
import {
  ADVANCED_FACE_DETECTION_EXTENSION_ID,
  HUMINT_EXTENSION_ID,
  useExtensionEnabled,
} from '@/hooks/use-extensions'
import { scoreColorSet as getScoreColor } from '@/utils/score-color'

interface CaseIdentityDetailPageProps {
  /** Optional caseId - if not provided, uses route params */
  caseId?: string
  /** Optional identityId - if not provided, uses route params */
  identityId?: string
  initialTab?: IdentityDetailTab
  /**
   * The identity was merged into another one. On its own route the page moves
   * to the survivor's by itself; a host that embeds it holds the selection, and
   * moves it here.
   */
  onMerged?: (survivorId: string) => void
}

export default function CaseIdentityDetailPage(props: CaseIdentityDetailPageProps) {
  const { t } = useTranslation()
  const params = useParams<{ caseId: string; identityId: string }>()
  // Use props if provided, otherwise fall back to route params
  const caseId = props.caseId || params.caseId || ''
  const rawIdentityId = props.identityId || params.identityId
  // Decode identityId in case it was URL-encoded in the route
  const identityId = rawIdentityId ? decodeURIComponent(rawIdentityId) : ''

  const detail = useIdentityDetail({ caseId, identityId })
  // The faces card's read of the same identity, watched here too: it is read
  // again after every face action and when the window regains focus, so it is
  // often the first to learn that the identity was merged away.
  const facesRead = useFaceMatchingIdentity(caseId, identityId || null, CARD_DETAIL_OPTIONS)
  // Merged into another identity: the page moves to the survivor (ADR-0052,
  // ADR-0053).
  const following = useIdentityPageFollowsMerge({
    caseId,
    identityId,
    named: detail.mergedInto ?? survivorNamedBy(facesRead),
    onMerged: props.onMerged,
  })

  if (!caseId || !identityId) {
    return (
      <div className="min-h-dvh bg-transparent flex items-center justify-center">
        <p className="text-muted-foreground">{t('identity.detail.missingParams')}</p>
      </div>
    )
  }

  // What the page holds besides the read is one identity's as well — an upload
  // in flight, the main photo just picked, the matches being decided — so
  // another identity gets a profile of its own.
  return (
    <IdentityProfile
      key={`${caseId}/${identityId}`}
      caseId={caseId}
      identityId={identityId}
      detail={{ ...detail, isLoading: detail.isLoading || following }}
      initialTab={props.initialTab}
    />
  )
}

interface IdentityProfileProps {
  caseId: string
  identityId: string
  /** The identity's read and actions; loading for as long as the page is about to move. */
  detail: UseIdentityDetailResult
  initialTab?: IdentityDetailTab
}

function IdentityProfile({ caseId, identityId, detail, initialTab }: IdentityProfileProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isLoading, error, openingGraph, updateField, openInGraphView, refresh } = detail
  // Faces, photos and matching are Advanced Face Detection's (ADR-0051): with it
  // off the card is the profile and its documents alone. The avatar stays.
  const humint = useExtensionEnabled(HUMINT_EXTENSION_ID)
  const faceDetection = useExtensionEnabled(ADVANCED_FACE_DETECTION_EXTENSION_ID)
  // The card's own data and actions; the header avatar re-reads after a new
  // main photo, so the page never reloads.
  const faces = useIdentityFaces(caseId, identityId, refresh)
  const queryClient = useQueryClient()
  // Confirming a match changes the identity's full-photo grid as well as its avatar.
  const onFaceDecided = useCallback(() => {
    void refresh()
    void queryClient.invalidateQueries({
      queryKey: ['case', caseId, 'identity-photos', identityId],
    })
  }, [caseId, identityId, queryClient, refresh])
  // The extraction wrote faces, photos and an ATTACHED_TO document — every part
  // of the page re-reads instead of guessing what changed (slice 05).
  const onPhotosExtracted = useCallback(() => {
    void refresh()
    invalidateFaceMatching(queryClient, caseId)
    void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'identity-photos'] })
    void queryClient.invalidateQueries({ queryKey: unassignedFacesKeys.all(caseId) })
  }, [caseId, queryClient, refresh])
  const attachment = useAttachIdentityDocuments(caseId, identityId, onPhotosExtracted)

  if (isLoading) {
    return (
      <PageShell
        layout="padded"
        icon={User}
        title="Identity"
        summary={t('identity.detail.loading')}
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
        </div>
      </PageShell>
    )
  }

  if (error || !data) {
    return (
      <PageShell
        layout="padded"
        icon={User}
        title="Identity"
        summary={error || t('identity.detail.notFound')}
      >
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{error || t('identity.detail.notFound')}</p>
          </CardContent>
        </Card>
      </PageShell>
    )
  }

  const { identity, documents, linkedDocuments = [] } = data

  // Check if person has at least one verified document
  const hasVerifiedDocument = documents.some(doc => doc.isVerified)

  const scoreResult = calculateIdentityScore(identity, documents, t)
  const scoreColors = getScoreColor(scoreResult.score)
  const scoreLabel = getScoreLabel(scoreResult.score, t)
  const graphStats = data.graphStats || { hop1: 0, hop2: 0 }

  return (
    <PageShell layout="padded" className="max-md:pt-4">
      <IdentityDetailLayout
        defaultTab={initialTab}
        header={
          <IdentityHeaderCard
            identity={identity}
            hasVerifiedDocument={hasVerifiedDocument}
            onUpdateField={updateField}
            actions={<IdentityHeaderActions caseId={caseId} identity={identity} />}
          />
        }
        overview={
          <div className="flex flex-col gap-6">
            <IdentityProfileFields identity={identity} onUpdateField={updateField} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <IdentityScoreCard
                scoreResult={scoreResult}
                scoreLabel={scoreLabel}
                colors={scoreColors}
              />
              <GraphEntryCard
                identity={identity}
                graphStats={graphStats}
                opening={openingGraph}
                onOpen={() => void openInGraphView()}
              />
            </div>

            <LinkedDocumentsList
              caseId={caseId}
              documents={{ linked: linkedDocuments, identification: documents }}
              onNavigateDocument={documentId =>
                navigate(`/cases/${caseId}/documents?doc=${encodeURIComponent(documentId)}`)
              }
              upload={{ state: attachment, onFiles: files => void attachment.addFiles(files) }}
            />

            {/* The same History card as the graph's entity detail (PRD-0075 R-C1). */}
            {identity.uid ? (
              <Card className="bg-card border-border">
                <CardContent className="pt-6 max-md:px-4">
                  <EntityHistoryOf
                    caseId={caseId}
                    uid={identity.uid}
                    onOpenDocument={documentId =>
                      navigate(`/cases/${caseId}/documents?doc=${encodeURIComponent(documentId)}`)
                    }
                  />
                </CardContent>
              </Card>
            ) : null}
          </div>
        }
        media={{
          audio: humint.enabled ? (
            <IdentityAudio caseId={caseId} identityId={identityId} />
          ) : undefined,
          faces: faceDetection.enabled
            ? faceSectionsOf({ caseId, identityId, faces, attachment, onFaceDecided })
            : undefined,
        }}
      />
    </PageShell>
  )
}

/** The header's buttons: *Sledovať* (PRD-0075 R-B1), OSINT and the PDF menu. */
function IdentityHeaderActions({ caseId, identity }: { caseId: string; identity: IdentityDetail }) {
  if (!caseId || !identity.id) return null
  return (
    <>
      <WatchToggle
        caseId={caseId}
        entity={{ uid: identity.uid ?? null, label: identity.name || null, entityType: 'Person' }}
      />
      <OsintEntityLaunchButton
        target={{
          caseId,
          entityId: identity.id,
          entityName: identity.name,
          // This page only ever renders a Person node.
          entityType: 'Person',
        }}
      />
      <GeneratePdfMenu subjectType="identity" subjectRef={identity.id} caseId={caseId} />
    </>
  )
}

interface FaceSectionsInput {
  caseId: string
  identityId: string
  faces: UseIdentityFacesResult
  attachment: UseAttachIdentityDocumentsResult
  onFaceDecided: () => void
}

/** The card's Advanced Face Detection tabs: faces, photos with their upload, possible matches. */
function faceSectionsOf({
  caseId,
  identityId,
  faces,
  attachment,
  onFaceDecided,
}: FaceSectionsInput): IdentityFaceSections {
  return {
    faces: <FacesCard detail={faces.detail} state={faces.state} actions={faces.actions} />,
    photos: <IdentityPhotos caseId={caseId} personId={identityId} />,
    photosAction: (
      <AddPhotoButton
        state={{
          uploading: attachment.uploading,
          processing: attachment.processing,
          uploaded: attachment.uploaded,
          total: attachment.total,
        }}
        onFiles={files => void attachment.addFiles(files)}
      />
    ),
    possibleMatches: (
      <Card className="bg-card border-border">
        <CardContent className="pt-6 max-md:px-4">
          {/* The tab's own star, table and modal — a decision here also
              re-reads the identity, because it can move the main photo. */}
          <IdentityMatches caseId={caseId} personId={identityId} onDecided={onFaceDecided} />
        </CardContent>
      </Card>
    ),
  }
}
